import {
  CustomerRequestDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
} from '@shared/services/apiService/apiService';
import { ResponseMsg } from '@shared/types/admin/agent';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreateCampaignFormValue } from '../model/type';
import { removeDuplicatePhones } from '../utils/remove-duplicates-phone';
import { campaignRequestMapper } from '../utils/campaign-mapper';

interface UseEditCampaignProps {
  CustomersListChanged: React.MutableRefObject<boolean>;
}

export const useEditCampaign = ({ CustomersListChanged }: UseEditCampaignProps) => {
  const [editCampaign] = useCampaignUpdateControllerCampaignUpdateMutation();
  const [responseMsg, setResponseMsg] = useState<ResponseMsg>({ message: '', type: 'success' });
  const [responseLoadingFlag, setResponseLoadingFlag] = useState(false);

  const formMethods = useForm<CreateCampaignFormValue>({
    mode: 'all',
    defaultValues: {
      id: '',
      title: '',
    },
  });

  const handleResponseMessage = (message: string, type: 'success' | 'error') => {
    setResponseMsg({ message, type });
  };

  const processCustomersList = useCallback(
    (data: CreateCampaignFormValue) => {
      const customers = CustomersListChanged.current
        ? data.customers?.filter((item) => item.id)
        : [];

      const uniqueCustomers = removeDuplicatePhones(customers || []);

      return uniqueCustomers as CustomerRequestDto[] | undefined;
    },
    [CustomersListChanged],
  );

  const saveCampaignForm = useCallback(
    async (data: CreateCampaignFormValue): Promise<boolean> => {
      setResponseLoadingFlag(true);
      setResponseMsg({ message: '', type: 'success' });

      try {
        const customers = CustomersListChanged.current ? processCustomersList(data) : undefined;
        const campaignUpdateRequestDto = campaignRequestMapper(data, customers);

        await editCampaign({ campaignUpdateRequestDto, id: data.id }).unwrap();
        handleResponseMessage('adminMainPage.campaigns.successfullyEdited', 'success');
        return true;
      } catch {
        handleResponseMessage('adminMainPage.campaigns.somethingWentWrong', 'error');
        return false;
      } finally {
        setResponseLoadingFlag(false);
      }
    },
    [CustomersListChanged, editCampaign, processCustomersList],
  );

  const onSubmit = formMethods.handleSubmit(
    async (data) => saveCampaignForm(data),
    // tweak to store data even if we have some invalid data
    async () => saveCampaignForm(formMethods.getValues()),
  );

  return {
    onSubmit,
    saveCampaignForm,
    setResponseMsg,
    responseMsg,
    responseLoadingFlag,
    formMethods,
  };
};
